'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Room, RoomEvent } from 'livekit-client';
import { motion } from 'motion/react';
import { RoomAudioRenderer, RoomContext, StartAudio } from '@livekit/components-react';
import { PopupView } from '@/components/embed-popup/popup-view';
import { Trigger } from '@/components/embed-popup/trigger';
import { Button } from '@/components/ui/button';
import useAgentConfig from '@/hooks/use-agent-config';
import useConnectionDetails from '@/hooks/use-connection-details';
import { resolveAppearance } from '@/lib/appearance';
import { cn } from '@/lib/utils';
import { CartProvider, useCart } from '@/hooks/use-cart';
function EmbedFixedAgentClientInner({ appConfig, agentId: initialAgentId, storeId }) {
    var _a, _b;
    // Resolve storeId to agentId if storeId is provided
    const [resolvedAgentId, setResolvedAgentId] = useState(initialAgentId);
    const [storeIdError, setStoreIdError] = useState(null);
    // Start in resolving state if we have storeId but no agentId
    const [isResolvingStoreId, setIsResolvingStoreId] = useState(!initialAgentId && !!storeId);
    // Use resolved agentId (from storeId lookup) or the direct agentId
    const agentId = resolvedAgentId;
    // Effect to resolve storeId to agentId
    useEffect(() => {
        // If agentId is provided directly, use it
        if (initialAgentId) {
            setResolvedAgentId(initialAgentId);
            return;
        }
        // If storeId is provided, resolve it to an agentId
        if (storeId) {
            setIsResolvingStoreId(true);
            setStoreIdError(null);
            const resolveStoreId = async () => {
                try {
                    // Determine the base URL for the API call
                    const embedBase = window.__LK_EMBED_BASE__;
                    const base = embedBase || window.location.origin;
                    const url = new URL('/api/agents/by-store', base);
                    url.searchParams.set('storeId', storeId);
                    const response = await fetch(url.toString(), {
                        cache: 'no-store',
                    });
                    if (!response.ok) {
                        const data = await response.json().catch(() => ({ error: 'Unknown error' }));
                        throw new Error(data.error || `Failed to resolve store: ${response.status}`);
                    }
                    const data = await response.json();
                    if (data.id) {
                        setResolvedAgentId(data.id);
                    }
                    else {
                        throw new Error('No agent linked to this store');
                    }
                }
                catch (error) {
                    console.error('Failed to resolve storeId to agentId:', error);
                    setStoreIdError(error instanceof Error ? error.message : 'Failed to resolve store');
                }
                finally {
                    setIsResolvingStoreId(false);
                }
            };
            resolveStoreId();
        }
    }, [initialAgentId, storeId]);
    const room = useMemo(() => new Room(), []);
    const [popupOpen, setPopupOpen] = useState(false);
    const [isExpanded, setIsExpanded] = useState(false);
    const [currentError, setCurrentError] = useState(null);
    const [sessionEnded, setSessionEnded] = useState(false);
    // Lightweight cart state for agent cart bridge
    const [cart, setCart] = useState({ items: [], updatedAt: 0 });
    const cartRef = useRef(cart);
    useEffect(() => {
        cartRef.current = cart;
    }, [cart]);
    const { ingestAgentCart } = useCart();
    const cartSyncRef = useRef(-1);
    useEffect(() => {
        if (!cart)
            return;
        if (cart.updatedAt === cartSyncRef.current)
            return;
        cartSyncRef.current = cart.updatedAt;
        ingestAgentCart(cart);
    }, [cart, ingestAgentCart]);
    const systemNoticeRef = useRef(false);
    const { config: agentConfig, error: agentConfigError } = useAgentConfig(agentId);
    const isConnectingRef = useRef(false);
    const closingRef = useRef(false);
    const restartInProgressRef = useRef(false);
    const restartTimeoutRef = useRef(null);
    // Track intentional disconnect caused by mode switching in hybrid agents
    const switchingRef = useRef(false);
    const [mode, setMode] = useState('chat');
    const { connectionDetails, error: connError, refreshConnectionDetails, } = useConnectionDetails(agentId, mode, storeId);
    // Compute allowed capabilities from agent type
    const agentType = (_a = agentConfig === null || agentConfig === void 0 ? void 0 : agentConfig.agentType) !== null && _a !== void 0 ? _a : 'hybrid';
    const supportsVoice = agentType === 'voice' || agentType === 'hybrid';
    // Initialize mode based on agent type and any stored preference
    useEffect(() => {
        if (!agentId)
            return;
        // Default to chat for hybrid; ignore stored preference to ensure consistent first open
        let initial = 'chat';
        const stored = typeof window !== 'undefined'
            ? window.localStorage.getItem(`lk-embed-mode:${agentId}`)
            : null;
        // Only apply stored preference for strict chat/voice agents
        if (agentType !== 'hybrid' && (stored === 'chat' || stored === 'voice'))
            initial = stored;
        // Enforce agent type constraints
        if (agentType === 'chat')
            initial = 'chat';
        if (agentType === 'voice')
            initial = 'voice';
        setMode(initial);
    }, [agentId, agentType]);
    // Persist mode for hybrid
    useEffect(() => {
        if (!agentId)
            return;
        if (agentType !== 'hybrid')
            return;
        try {
            window.localStorage.setItem(`lk-embed-mode:${agentId}`, mode);
        }
        catch (_a) { }
    }, [mode, agentId, agentType]);
    // On mode change for hybrid, reconnect with a fresh session (new roomName token)
    useEffect(() => {
        if (!agentId)
            return;
        if (agentType !== 'hybrid')
            return;
        // Disconnect any existing session before fetching new connection details
        if (room.state !== 'disconnected') {
            switchingRef.current = true;
            room.disconnect();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, agentId, agentType]);
    const assetBase = useMemo(() => {
        if (typeof window === 'undefined')
            return '';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const base = window.__LK_EMBED_BASE__;
        return base !== null && base !== void 0 ? base : window.location.origin;
    }, []);
    const logoSrc = `${assetBase}/LOGO-02.png`;
    const appearance = useMemo(() => resolveAppearance(agentConfig !== null && agentConfig !== void 0 ? agentConfig : null), [agentConfig]);
    const widgetPosition = (_b = appearance.widgetPosition) !== null && _b !== void 0 ? _b : 'right';
    const handleTogglePopup = () => {
        const willOpen = !popupOpen;
        if (willOpen) {
            // When opening, re-apply any persisted errors
            // This ensures errors show again after being dismissed and reopened
            // Priority: storeIdError > connError > agentConfigError
            if (connectionDetails) {
                // If we have valid connection details, clear any stale errors
                setCurrentError(null);
            }
            else if (storeIdError) {
                setCurrentError({ title: 'Unable to load store configuration', description: storeIdError });
            }
            else if (connError) {
                setCurrentError({ title: 'Unable to start agent', description: connError });
            }
            else if (agentConfigError && (resolvedAgentId || !storeId)) {
                setCurrentError({ title: 'Unable to load agent configuration', description: agentConfigError });
            }
            // If opening popup after a previous session ended, refresh connection details
            if (room.state === 'disconnected') {
                setSessionEnded(false);
                refreshConnectionDetails();
            }
            setPopupOpen(true);
        }
        else {
            // When closing, dismiss any error and close the popup
            if (currentError) {
                handleDismissError();
            }
            else {
                setPopupOpen(false);
            }
        }
    };
    const handleDismissError = () => {
        // Mark as closing to prevent any reconnection attempts
        closingRef.current = true;
        systemNoticeRef.current = false;
        // Close popup first to prevent auto-reconnect triggers
        setPopupOpen(false);
        setCurrentError(null);
        // Then disconnect the room
        try {
            room.disconnect();
        }
        catch (_a) { }
        // Do NOT refresh connection details - just close the widget
    };
    const handleEndSession = () => {
        // Disconnect the room and mark intentional close
        closingRef.current = true;
        try {
            room.disconnect();
        }
        catch (_a) { }
        setPopupOpen(false);
        setCurrentError(null);
        setSessionEnded(false);
        // Refresh connection details so next open gets a fresh session
        refreshConnectionDetails();
    };
    const handleRestartSession = () => {
        setSessionEnded(false);
        restartInProgressRef.current = true;
        if (restartTimeoutRef.current) {
            clearTimeout(restartTimeoutRef.current);
        }
        restartTimeoutRef.current = setTimeout(() => {
            restartInProgressRef.current = false;
            restartTimeoutRef.current = null;
        }, 20000);
        const refresh = () => {
            refreshConnectionDetails();
        };
        // If we're already disconnected (e.g., ended due to inactivity), skip waiting and just fetch a new token.
        if (room.state === 'disconnected') {
            refresh();
            return;
        }
        // Disconnect first, then refresh connection details once the room is fully disconnected.
        closingRef.current = true;
        const onDisconnectedOnce = () => {
            room.off(RoomEvent.Disconnected, onDisconnectedOnce);
            refresh();
        };
        room.on(RoomEvent.Disconnected, onDisconnectedOnce);
        try {
            room.disconnect();
        }
        catch (_a) {
            room.off(RoomEvent.Disconnected, onDisconnectedOnce);
            refresh();
        }
    };
    // When connection error occurs, just set the error state.
    // Don't auto-open the popup - let the user click to see the error.
    useEffect(() => {
        if (connError) {
            setCurrentError({ title: 'Unable to start agent', description: connError });
        }
    }, [connError]);
    // When agent config error occurs, just set the error state.
    // Don't auto-open the popup - let the user click to see the error.
    useEffect(() => {
        // Don't show agent config error while we're still resolving storeId
        if (isResolvingStoreId)
            return;
        // Only show error if we have a resolved agentId but config failed,
        // or if we don't have storeId (direct agentId mode)
        if (agentConfigError && (resolvedAgentId || !storeId)) {
            setCurrentError({
                title: 'Unable to load agent configuration',
                description: agentConfigError,
            });
        }
    }, [agentConfigError, isResolvingStoreId, storeId, resolvedAgentId]);
    // When storeId resolution error occurs, just set the error state.
    // Don't auto-open the popup - let the user click to see the error.
    useEffect(() => {
        if (storeIdError) {
            setCurrentError({
                title: 'Unable to load store configuration',
                description: storeIdError,
            });
        }
    }, [storeIdError]);
    useEffect(() => {
        const onConnected = () => {
            if (!restartInProgressRef.current)
                return;
            restartInProgressRef.current = false;
            if (restartTimeoutRef.current) {
                clearTimeout(restartTimeoutRef.current);
                restartTimeoutRef.current = null;
            }
            setSessionEnded(false);
        };
        const onParticipantDisconnected = (participant) => {
            var _a;
            const identityRaw = participant === null || participant === void 0 ? void 0 : participant.identity;
            const identity = typeof identityRaw === 'string' ? identityRaw : String(identityRaw !== null && identityRaw !== void 0 ? identityRaw : '');
            const localIdentity = (_a = room.localParticipant) === null || _a === void 0 ? void 0 : _a.identity;
            const isLocal = Boolean(localIdentity && identity && localIdentity === identity);
            const isUserIdentity = identity.startsWith('voice_assistant_user_') || identity.includes('voice_assistant_user');
            const isLikelyAgent = identity.startsWith('agent-') ||
                identity.startsWith('agent_') ||
                (identity.includes('voice_assistant') && !identity.includes('_user_') && !isUserIdentity);
            if (!identity || isLocal || isUserIdentity || !isLikelyAgent) {
                return;
            }
            // Check if the disconnected participant is the agent
            console.log('Agent participant disconnected:', identity);
            // Don't show session ended if we're intentionally closing or restarting
            if (closingRef.current || switchingRef.current || restartInProgressRef.current) {
                return;
            }
            // Show session ended message in chat mode
            if (mode === 'chat') {
                setSessionEnded(true);
            }
            else {
                setPopupOpen(false);
            }
        };
        const onDisconnected = () => {
            if (closingRef.current) {
                closingRef.current = false;
                setSessionEnded(false);
                return;
            }
            if (switchingRef.current) {
                // Hybrid mode switch: keep popup open and reconnect to the new mode
                switchingRef.current = false;
                if (popupOpen) {
                    refreshConnectionDetails();
                }
                return;
            }
            if (restartInProgressRef.current) {
                return;
            }
            if (systemNoticeRef.current) {
                setTimeout(() => {
                    systemNoticeRef.current = false;
                    setCurrentError(null);
                }, 6000);
                return;
            }
            // Remote disconnect (inactivity/max length): show session ended message in chat, close in voice
            if (mode === 'chat') {
                setSessionEnded(true);
            }
            else {
                setPopupOpen(false);
            }
            setCurrentError(null);
        };
        const onMediaDevicesError = (error) => {
            setCurrentError({
                title: 'Encountered an error with your media devices',
                description: `${error.name}: ${error.message}`,
            });
        };
        room.on(RoomEvent.Connected, onConnected);
        room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
        room.on(RoomEvent.MediaDevicesError, onMediaDevicesError);
        room.on(RoomEvent.Disconnected, onDisconnected);
        return () => {
            room.off(RoomEvent.Connected, onConnected);
            room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
            room.off(RoomEvent.Disconnected, onDisconnected);
            room.off(RoomEvent.MediaDevicesError, onMediaDevicesError);
        };
    }, [room, refreshConnectionDetails, popupOpen, mode]);
    useEffect(() => {
        if (!popupOpen) {
            return;
        }
        // When the session has ended (e.g., inactivity timeout), avoid auto-reconnecting
        // until the user explicitly starts a new conversation.
        if (sessionEnded) {
            return;
        }
        if (room.state !== 'disconnected') {
            return;
        }
        if (!connectionDetails) {
            return;
        }
        // Don't auto-connect if we're in the process of closing
        if (closingRef.current) {
            return;
        }
        const connect = async () => {
            if (isConnectingRef.current)
                return;
            isConnectingRef.current = true;
            try {
                await room.connect(connectionDetails.serverUrl, connectionDetails.participantToken);
                if (supportsVoice && mode === 'voice') {
                    await room.localParticipant.setMicrophoneEnabled(true, undefined, {
                        preConnectBuffer: appConfig.isPreConnectBufferEnabled,
                    });
                }
            }
            catch (error) {
                if (error instanceof Error) {
                    console.error('Error connecting to agent:', error);
                    setCurrentError({
                        title: 'There was an error connecting to the agent',
                        description: `${error.name}: ${error.message}`,
                    });
                }
            }
            finally {
                isConnectingRef.current = false;
            }
        };
        connect();
        // Do not forcibly disconnect on dependency changes; only disconnect when
        // the user explicitly closes the popup or navigates away.
    }, [
        room,
        popupOpen,
        sessionEnded,
        connectionDetails,
        appConfig.isPreConnectBufferEnabled,
        supportsVoice,
        mode,
    ]);
    // Listen for system notices (quota exceeded) over LiveKit data/text streams
    useEffect(() => {
        var _a;
        const encoder = new TextEncoder();
        const handleData = (payload, _participant, _kind, topic) => {
            var _a, _b, _c, _d, _e, _f;
            // System notices
            if (!topic || topic === 'system') {
                try {
                    const txt = new TextDecoder().decode(payload);
                    const msg = JSON.parse(txt);
                    if ((msg === null || msg === void 0 ? void 0 : msg.type) === 'system') {
                        systemNoticeRef.current = true;
                        setCurrentError({ title: 'Usage Limit Reached', description: (_a = msg.message) !== null && _a !== void 0 ? _a : 'Your plan limits have been reached.' });
                        if (!popupOpen)
                            setPopupOpen(true);
                        if (supportsVoice && mode === 'voice') {
                            try {
                                closingRef.current = true;
                                room.disconnect();
                            }
                            catch (_g) { }
                            setPopupOpen(false);
                        }
                        return;
                    }
                }
                catch (_h) {
                    // ignore
                }
            }
            // Cart bridge
            if (topic === 'cart') {
                try {
                    const txt = new TextDecoder().decode(payload);
                    const msg = JSON.parse(txt);
                    if ((msg === null || msg === void 0 ? void 0 : msg.type) === 'cart_action') {
                        const requestId = msg.request_id;
                        const action = msg.action;
                        const publishResult = (success, snapshot) => {
                            const response = { type: 'cart_result', request_id: requestId, success, cart: snapshot !== null && snapshot !== void 0 ? snapshot : cartRef.current };
                            try {
                                room.localParticipant.publishData(encoder.encode(JSON.stringify(response)), {
                                    reliable: true,
                                    topic: 'cart',
                                }).catch(() => { });
                            }
                            catch (_a) { }
                        };
                        // Only compare 'name' and 'value' keys for variant matching - other metadata keys
                        // like group_id, price_settings, type, group_name are not relevant for identifying items
                        const eqCustom = (a, b) => {
                            const A = Array.isArray(a) ? a : [];
                            const B = Array.isArray(b) ? b : [];
                            if (A.length !== B.length)
                                return false;
                            return A.every((ai, i) => {
                                var _a, _b, _c, _d;
                                const bi = B[i];
                                if (typeof ai !== 'object' || typeof bi !== 'object' || ai === null || bi === null)
                                    return false;
                                // Compare only the meaningful variant keys: name and value
                                const aName = String((_a = ai.name) !== null && _a !== void 0 ? _a : '').toLowerCase().trim();
                                const aValue = String((_b = ai.value) !== null && _b !== void 0 ? _b : '').toLowerCase().trim();
                                const bName = String((_c = bi.name) !== null && _c !== void 0 ? _c : '').toLowerCase().trim();
                                const bValue = String((_d = bi.value) !== null && _d !== void 0 ? _d : '').toLowerCase().trim();
                                return aName === bName && aValue === bValue;
                            });
                        };
                        if (action === 'add_product') {
                            const pid = String((_b = msg.product_id) !== null && _b !== void 0 ? _b : '').trim();
                            const qtyVal = Number((_c = msg.quantity) !== null && _c !== void 0 ? _c : 1);
                            const qty = Number.isFinite(qtyVal) && qtyVal > 0 ? Math.floor(qtyVal) : 1;
                            if (!pid) {
                                publishResult(false);
                                return;
                            }
                            const hasCustom = Array.isArray(msg.custom_fields) && msg.custom_fields.length > 0;
                            let snapshot = null;
                            setCart((prev) => {
                                var _a, _b;
                                const next = { items: [...prev.items], updatedAt: Date.now() };
                                if (!hasCustom) {
                                    const ix = next.items.findIndex((it) => it.product_id === pid && (!it.custom_fields || (Array.isArray(it.custom_fields) && it.custom_fields.length === 0)));
                                    if (ix >= 0) {
                                        next.items[ix] = Object.assign(Object.assign({}, next.items[ix]), { quantity: (next.items[ix].quantity || 0) + qty });
                                    }
                                    else {
                                        next.items.push({ product_id: pid, quantity: qty, product: (_a = msg.product) !== null && _a !== void 0 ? _a : null, custom_fields: null });
                                    }
                                }
                                else {
                                    next.items.push({ product_id: pid, quantity: qty, product: (_b = msg.product) !== null && _b !== void 0 ? _b : null, custom_fields: msg.custom_fields });
                                }
                                snapshot = next;
                                return next;
                            });
                            publishResult(true, snapshot !== null && snapshot !== void 0 ? snapshot : cartRef.current);
                            return;
                        }
                        if (action === 'add_products') {
                            const list = Array.isArray(msg.items) ? msg.items : [];
                            if (!list.length) {
                                publishResult(false);
                                return;
                            }
                            let snapshot = null;
                            setCart((prev) => {
                                var _a, _b, _c, _d;
                                const next = { items: [...prev.items], updatedAt: Date.now() };
                                for (const entry of list) {
                                    const pid = String((_a = entry === null || entry === void 0 ? void 0 : entry.product_id) !== null && _a !== void 0 ? _a : '').trim();
                                    const qtyVal = Number((_b = entry === null || entry === void 0 ? void 0 : entry.quantity) !== null && _b !== void 0 ? _b : 1);
                                    const qty = Number.isFinite(qtyVal) && qtyVal > 0 ? Math.floor(qtyVal) : 1;
                                    if (!pid)
                                        continue;
                                    const hasCustom = Array.isArray(entry === null || entry === void 0 ? void 0 : entry.custom_fields) && entry.custom_fields.length > 0;
                                    if (!hasCustom) {
                                        const ix = next.items.findIndex((it) => it.product_id === pid && (!it.custom_fields || (Array.isArray(it.custom_fields) && it.custom_fields.length === 0)));
                                        if (ix >= 0) {
                                            next.items[ix] = Object.assign(Object.assign({}, next.items[ix]), { quantity: (next.items[ix].quantity || 0) + qty });
                                        }
                                        else {
                                            next.items.push({ product_id: pid, quantity: qty, product: (_c = entry === null || entry === void 0 ? void 0 : entry.product) !== null && _c !== void 0 ? _c : null, custom_fields: null });
                                        }
                                    }
                                    else {
                                        next.items.push({ product_id: pid, quantity: qty, product: (_d = entry === null || entry === void 0 ? void 0 : entry.product) !== null && _d !== void 0 ? _d : null, custom_fields: entry.custom_fields });
                                    }
                                }
                                snapshot = next;
                                return next;
                            });
                            publishResult(true, snapshot !== null && snapshot !== void 0 ? snapshot : cartRef.current);
                            return;
                        }
                        if (action === 'get_cart') {
                            publishResult(true);
                            return;
                        }
                        if (action === 'update_item') {
                            const pid = String((_d = msg.product_id) !== null && _d !== void 0 ? _d : '').trim();
                            const qtyVal = Number((_e = msg.quantity) !== null && _e !== void 0 ? _e : 0);
                            const qty = Number.isFinite(qtyVal) && qtyVal >= 0 ? Math.floor(qtyVal) : 0;
                            if (!pid) {
                                publishResult(false);
                                return;
                            }
                            let snapshot = null;
                            let itemFound = false;
                            setCart((prev) => {
                                const next = { items: [...prev.items], updatedAt: Date.now() };
                                const targetFields = Array.isArray(msg.custom_fields) ? msg.custom_fields : null;
                                const idx = next.items.findIndex((it) => { var _a; return it.product_id === pid && (targetFields ? eqCustom((_a = it.custom_fields) !== null && _a !== void 0 ? _a : null, targetFields) : !it.custom_fields || (Array.isArray(it.custom_fields) && it.custom_fields.length === 0)); });
                                if (idx >= 0) {
                                    itemFound = true;
                                    if (qty <= 0) {
                                        next.items.splice(idx, 1);
                                    }
                                    else {
                                        next.items[idx] = Object.assign(Object.assign({}, next.items[idx]), { quantity: qty });
                                    }
                                }
                                snapshot = next;
                                return next;
                            });
                            publishResult(itemFound, snapshot !== null && snapshot !== void 0 ? snapshot : cartRef.current);
                            return;
                        }
                        if (action === 'remove_item') {
                            const pid = String((_f = msg.product_id) !== null && _f !== void 0 ? _f : '').trim();
                            if (!pid) {
                                publishResult(false);
                                return;
                            }
                            let snapshot = null;
                            let itemFound = false;
                            setCart((prev) => {
                                const next = { items: [...prev.items], updatedAt: Date.now() };
                                const targetFields = Array.isArray(msg.custom_fields) ? msg.custom_fields : null;
                                const idx = next.items.findIndex((it) => { var _a; return it.product_id === pid && (targetFields ? eqCustom((_a = it.custom_fields) !== null && _a !== void 0 ? _a : null, targetFields) : !it.custom_fields || (Array.isArray(it.custom_fields) && it.custom_fields.length === 0)); });
                                if (idx >= 0) {
                                    next.items.splice(idx, 1);
                                    itemFound = true;
                                }
                                snapshot = next;
                                return next;
                            });
                            publishResult(itemFound, snapshot !== null && snapshot !== void 0 ? snapshot : cartRef.current);
                            return;
                        }
                    }
                }
                catch (_j) {
                    // ignore malformed cart payloads
                }
            }
        };
        const handleText = (text, _participant, topic) => {
            var _a;
            if (topic && topic !== 'system')
                return;
            try {
                const msg = JSON.parse(text);
                if ((msg === null || msg === void 0 ? void 0 : msg.type) === 'system') {
                    systemNoticeRef.current = true;
                    setCurrentError({ title: 'Usage Limit Reached', description: (_a = msg.message) !== null && _a !== void 0 ? _a : 'Your plan limits have been reached.' });
                    if (!popupOpen)
                        setPopupOpen(true);
                    if (supportsVoice && mode === 'voice') {
                        try {
                            closingRef.current = true;
                            room.disconnect();
                        }
                        catch (_b) { }
                        setPopupOpen(false);
                    }
                }
            }
            catch (_c) {
                // ignore
            }
        };
        room.on(RoomEvent.DataReceived, handleData);
        // Some SDK versions emit TextReceived
        // @ts-ignore
        (_a = room.on) === null || _a === void 0 ? void 0 : _a.call(room, RoomEvent.TextReceived, handleText);
        return () => {
            var _a;
            room.off(RoomEvent.DataReceived, handleData);
            // @ts-ignore
            (_a = room.off) === null || _a === void 0 ? void 0 : _a.call(room, RoomEvent.TextReceived, handleText);
        };
    }, [room, popupOpen]);
    // Toggle mic when switching modes during an active session
    useEffect(() => {
        if (room.state === 'connected') {
            const enable = supportsVoice && mode === 'voice';
            room.localParticipant.setMicrophoneEnabled(enable).catch((err) => {
                console.error('Error toggling microphone:', err);
            });
        }
    }, [mode, supportsVoice, room]);
    // Note: We no longer auto-disconnect when popup closes.
    // The session stays alive when minimized. User must explicitly end the session.
    return (_jsxs(RoomContext.Provider, { value: room, children: [supportsVoice && mode === 'voice' ? (_jsxs(_Fragment, { children: [_jsx(RoomAudioRenderer, {}), _jsx(StartAudio, { label: "Start Audio" })] })) : null, popupOpen && isExpanded && (_jsx(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.3 }, className: "fixed inset-0 z-40 bg-black/20 backdrop-blur-sm sm:hidden", onClick: () => setIsExpanded(false) })), !isResolvingStoreId && (agentConfig || storeIdError || connError || agentConfigError) && (_jsx(Trigger, { error: !!currentError || !!storeIdError || !!connError || !!agentConfigError, popupOpen: popupOpen, onToggle: handleTogglePopup, agentType: agentType, mode: mode, primaryColor: appearance.primaryColor, widgetPosition: widgetPosition })), _jsx(motion.div, { initial: {
                    opacity: 0,
                    scale: 0.95,
                }, animate: {
                    opacity: popupOpen ? 1 : 0,
                    scale: popupOpen ? 1 : 0.95,
                    pointerEvents: popupOpen ? 'auto' : 'none',
                }, transition: {
                    duration: 0.3,
                    ease: 'easeOut',
                }, className: cn('fixed shadow-2xl flex flex-col overflow-hidden z-50 ring-1 ring-primary/5 transition-all duration-300', isExpanded
                    ? 'inset-4 sm:inset-6 rounded-2xl sm:rounded-3xl'
                    : cn('bottom-4 sm:bottom-6 w-full sm:w-[420px] h-[600px] sm:h-[720px] max-h-[90vh] rounded-2xl sm:rounded-3xl', 
                    // Apply positioning based on widgetPosition (for both mobile and desktop)
                    widgetPosition === 'left'
                        ? 'left-4 sm:left-6'
                        : widgetPosition === 'center'
                            ? 'left-1/2 -translate-x-1/2'
                            : 'right-4 sm:right-6')), style: {
                    pointerEvents: popupOpen ? 'auto' : 'none',
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(0, 0, 0, 0.05)',
                    border: '1px solid #e5e7eb',
                    backgroundColor: '#ffffff'
                }, children: _jsx("div", { className: "h-full w-full overflow-hidden", children: _jsxs("div", { className: "relative h-full w-full", children: [currentError && (_jsxs("div", { className: "absolute inset-0 z-20 flex h-full w-full flex-col items-center justify-center gap-6 p-8", style: { backgroundColor: '#ffffff' }, children: [_jsx("div", { className: "flex items-center justify-center", children: _jsx("div", { className: "flex h-16 w-16 sm:h-20 sm:w-20 items-center justify-center rounded-full bg-red-50", children: _jsx("svg", { className: "h-8 w-8 sm:h-10 sm:w-10 text-red-500", fill: "none", viewBox: "0 0 24 24", stroke: "currentColor", children: _jsx("path", { strokeLinecap: "round", strokeLinejoin: "round", strokeWidth: 2, d: "M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" }) }) }) }), _jsxs("div", { className: "flex w-full max-w-sm flex-col justify-center gap-3 text-center px-4", children: [_jsx("h3", { className: "text-lg sm:text-xl font-bold tracking-tight", style: { color: '#111827' }, children: currentError === null || currentError === void 0 ? void 0 : currentError.title }), _jsx("p", { className: "text-sm sm:text-base leading-relaxed", style: { color: '#6b7280' }, children: currentError === null || currentError === void 0 ? void 0 : currentError.description })] }), _jsx(Button, { onClick: handleDismissError, className: "mt-2 px-8 py-2.5 rounded-xl bg-primary hover:bg-primary/90 text-primary-foreground font-medium shadow-sm transition-all hover:scale-105 active:scale-95", children: "Close" })] })), _jsx("div", { className: cn('h-full w-full', currentError && 'invisible'), children: _jsx(PopupView, { disabled: !popupOpen, sessionStarted: popupOpen, onDisplayError: setCurrentError, agentType: agentType, mode: mode, onModeChange: agentType === 'hybrid' ? setMode : undefined, agentName: appearance.brandName, logoUrl: logoSrc, onClose: handleTogglePopup, onEndSession: handleEndSession, onRestartSession: handleRestartSession, sessionEnded: sessionEnded, appearance: appearance, isExpanded: isExpanded, onToggleExpanded: () => setIsExpanded(!isExpanded) }) })] }) }) })] }));
}
export default function EmbedFixedAgentClient(props) {
    return (_jsx(CartProvider, { children: _jsx(EmbedFixedAgentClientInner, Object.assign({}, props)) }));
}
